import { useState } from 'react';
import { Search, Download } from 'lucide-react';
import { useDailyAttendance } from '@/api/queries/attendance.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { format, subDays, addDays } from 'date-fns';

export default function AttendanceDailyPage() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [status, setStatus] = useState('All');
  const [search, setSearch] = useState('');

  const { data: resp, isLoading } = useDailyAttendance({ date, status: status === 'All' ? undefined : status });
  const all = resp?.data || [];
  const records = search ? all.filter(r => r.name?.toLowerCase().includes(search.toLowerCase())) : all;
  const isToday = date === format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Daily Register"
        breadcrumb={[{ label: 'Attendance', href: '/attendance' }, { label: 'Daily Register' }]}
        actions={<Button variant="outline" size="sm" icon={Download}>Export</Button>}
      />

      <div className="card flex items-center justify-between p-3">
        <button onClick={() => setDate(d => format(subDays(new Date(d), 1), 'yyyy-MM-dd'))}
          className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">←</button>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="text-[14px] font-semibold text-[var(--text-primary)] bg-transparent border-none outline-none cursor-pointer"/>
          {!isToday && <button onClick={() => setDate(format(new Date(), 'yyyy-MM-dd'))}
            className="text-[11px] font-semibold text-[var(--accent)] bg-[var(--accent)]/10 px-2 py-0.5 rounded-full">Today</button>}
        </div>
        <button onClick={() => setDate(d => format(addDays(new Date(d), 1), 'yyyy-MM-dd'))} disabled={isToday}
          className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)] disabled:opacity-30">→</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee…"
            className="w-full pl-8 pr-3 py-2 text-[13px] border border-[var(--border)] rounded-[8px] bg-[var(--bg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"/>
        </div>
        <div className="flex gap-1.5">
          {['All','PRESENT','ABSENT','LATE','ON_LEAVE'].map(t => (
            <button key={t} onClick={() => setStatus(t)}
              className={cn('px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors',
                status === t ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]')}>
              {t === 'All' ? `All (${all.length})` : t.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        {isLoading ? <div className="py-16 text-center text-[14px] text-[var(--text-tertiary)]">Loading…</div> : (
          <table className="w-full">
            <thead><tr className="border-b border-[var(--border)]">
              {['Employee','Dept','Check In','Check Out','Hours','Method','Status'].map(h => (
                <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-[var(--border)]">
              {records.map(r => (
                <tr key={r.employee_id} className="hover:bg-[var(--bg-muted)]">
                  <td className="px-5 py-3.5"><div className="flex items-center gap-3">
                    <Avatar name={r.name} size="sm"/>
                    <div><p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.name}</p>
                    <p className="text-[11px] text-[var(--text-tertiary)]">{r.employee_code}</p></div>
                  </div></td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{r.department?.name || r.department || '—'}</td>
                  <td className="px-5 py-3.5 text-[13px] font-medium text-[var(--text-primary)]">{r.check_in || '—'}</td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{r.check_out || '—'}</td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{r.hours_worked ? `${r.hours_worked}h` : '—'}</td>
                  <td className="px-5 py-3.5">{r.method ? <Badge variant="default" size="sm">{r.method}</Badge> : <span className="text-[12px] text-[var(--text-tertiary)]">—</span>}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={r.status}/></td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No records for this date</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
